import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { PipesModule } from '../../../shared/pipes/pipes.module';
import { MediaService } from '../../../shared/services/media.service';
import {
  ACCEPT_IMAGE_MIME,
  getMediaUploadFileError,
  IMAGE_MIME_EXTENSION_FALLBACKS,
} from '../../../shared/utils/accepted-image-formats.util';

const DONE_ICON = 'done';
const BLOCK_ICON = 'done';

/** When the browser omits/mislabels MIME, accept by filename extension. */
const MIME_EXTENSION_FALLBACKS: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.google-earth.kml+xml': ['.kml'],
  'application/vnd.google-earth.kmz': ['.kmz'],
  ...IMAGE_MIME_EXTENSION_FALLBACKS,
};

@Component({
  selector: 'app-upload-file-with-progress',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressBarModule,
    PipesModule,
    TranslateModule,
    MatIconModule,
  ],
  templateUrl: './upload-file-with-progress.component.html',
  styleUrls: ['./upload-file-with-progress.component.scss'],
})
export class UploadFileWithProgressComponent implements OnInit {
  /**
   * Image-oriented default (was image/*). PDF/audio callers must override
   * (guide → application/pdf, scenario-item/references → their accept).
   */
  @Input() accept = ACCEPT_IMAGE_MIME;
  @Input() isDropFile = false;
  @Input() aligned = false;
  @Input() factoryOfUploadFunction: (file: File) => Observable<any>;
  @Input() isShowButton = true;
  @Input() incomeFile = null;
  @Output() completed = new EventEmitter<any>();
  @Output() whenUpload: EventEmitter<any> = new EventEmitter();
  @Output() whenRemoveFile: EventEmitter<any> = new EventEmitter();
  @ViewChild('fileInput') fileInput: ElementRef;
  progress = 0;
  icon = null;
  file: File = null;
  /** Display name after server conversion (e.g. HEIC → PNG). */
  uploadedFileName: string | null = null;
  correctSize = true;
  maxVideoSize = 512;
  isSuccess = false;
  date = new Date();
  isDragging = false;
  constructor(
    private toastrService: ToastrService,
    private mediaService: MediaService,
  ) {}

  ngOnInit() {
    if (this.incomeFile) {
      this.file = this.incomeFile;
      this.progress = 100;
    }
  }

  fileInputOnChange(event, isFile = false) {
    this.icon = null;
    const selected: File = isFile ? event : event.target.files[0];
    if (!selected) {
      return;
    }

    if (!this.isFileAccepted(selected)) {
      this.toastrService.error(
        `Invalid file type. Accepted: ${this.accept}`,
      );
      this.file = null;
      this.progress = 0;
      if (!isFile && event?.target) {
        event.target.value = '';
      }
      return;
    }

    this.file = selected;
    this.uploadedFileName = null;
    this.progress = 0;
    if (this.file && !this.file.type.includes('video')) {
      this.correctSize = this.file?.size / 1024 / 1024 <= 2;
      if (this.isDropFile) {
        this.upload();
      }
    } else {
      this.handleFileSize();
    }
  }

  private isFileAccepted(file: File): boolean {
    if (!this.accept) {
      return true;
    }
    const accepted = this.accept
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean);
    if (!accepted.length) {
      return true;
    }

    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();

    return accepted.some((pattern) => {
      if (pattern.startsWith('.')) {
        return name.endsWith(pattern);
      }
      if (pattern.endsWith('/*')) {
        return type.startsWith(pattern.slice(0, -1));
      }
      if (type && type === pattern) {
        return true;
      }
      // Chromium often reports KML as empty / application/xml / text/xml.
      const fallbackExts = MIME_EXTENSION_FALLBACKS[pattern];
      if (fallbackExts?.some((ext) => name.endsWith(ext))) {
        return true;
      }
      return false;
    });
  }

  handleFileSize() {
    if (this.file?.size / 1024 / 1024 > this.maxVideoSize) {
      this.toastrService.error(
        `The video size exceeds ${this.maxVideoSize} MB. Please select a different file.`,
      );
      this.file = null;
    } else if (this.isDropFile) {
      this.upload();
    }
  }
  removeFile() {
    this.file = null;
    this.uploadedFileName = null;
    this.progress = 0;
    this.whenRemoveFile.emit();
  }

  get displayFileName(): string {
    return this.uploadedFileName || this.file?.name || '';
  }
  upload() {
    this.icon = null;
    this.isSuccess = false;
    this.mediaService.uploadMedia({ file: this.file }, true).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          // Keep bar moving; conversion may finish after bytes are sent.
          this.progress = Math.min(
            99,
            Math.round((event.loaded / event.total) * 100),
          );
        } else if (event.type === HttpEventType.Response) {
          this.progress = 100;
          const body = event.body;
          // HEIC/HEIF/BMP come back as PNG — show converted name when present.
          if (body?.name) {
            this.uploadedFileName = body.name;
          }
          this.completed.emit(body);
          this.icon = DONE_ICON;
          this.whenUpload.emit(event);
          this.isSuccess = true;
          if (!this.isDropFile) {
            this.file = null;
          }
        }
      },
      error: (error) => {
        this.icon = BLOCK_ICON;
        this.isSuccess = false;
        const message =
          getMediaUploadFileError(error) ||
          'Upload failed. Please try another image.';
        this.toastrService.error(message);
      },
    });
  }
  clickFileInput() {
    this.fileInput.nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
      Array.from(event.dataTransfer?.files).forEach((file) => {
        this.fileInputOnChange(file, true);
      });
    }
  }
}
